import { useId, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { Copy, Download, FlaskConical, KeyRound, Network, Radar, Route, ScanSearch, Share2, ShieldOff, Users } from 'lucide-react';
import { agentDownloadsApi, findingsApi, internalAgentsApi } from '../lib/api-client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { Input, Label, FieldError } from '../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { LoadingState, ErrorState, EmptyState, friendlyErrorMessage } from '../components/ui/states';
import { useToast } from '../components/ui/toast';
import { cn } from '../lib/cn';
import { can } from '../lib/rbac';
import { useAuth } from '../lib/auth-context';
import { EXPLOITATION_CAPABILITIES, findingsForCapability, type ExploitationCapability } from '../lib/exploitation-capabilities';
import { isPrivateIPv4 } from './QuickScan';
import type { InternalAgent, InternalAgentToken, FindingRecord } from '../lib/types';

/** Anchor target for the empty-state's "go to the download section" link below. */
const CONNECT_SECTION_ID = 'internal-testing-connect';

function StatusDot({ connected }: { connected: boolean }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={cn('inline-block h-2 w-2 rounded-full', connected ? 'bg-success' : 'bg-ink-faint')} />
      {connected ? 'Connected' : 'Disconnected'}
    </span>
  );
}

/** One agent row's Findings-column cell — mirrors EndpointTesting.tsx's FindingsCell exactly (a
 * per-hostname count fetched via useQueries below, linking into the Findings page pre-filtered to
 * this agent's hostname, see Findings.tsx's ?target= support). */
function FindingsCell({ hostname, count, isLoading }: { hostname: string; count: number | undefined; isLoading: boolean }) {
  if (isLoading || count === undefined) return <span className="text-ink-faint">…</span>;
  return (
    <Link to={`/findings?target=${encodeURIComponent(hostname)}`} className="font-medium text-brand underline-offset-2 hover:underline">
      {count} finding{count === 1 ? '' : 's'} →
    </Link>
  );
}

/**
 * "Start Scan" dialog for one connected internal agent — validates an RFC 1918 IP or CIDR range
 * client-side (reusing QuickScan.tsx's own `isPrivateIPv4`, which already accepts a CIDR suffix),
 * then hands off to the existing Quick Scan wizard via its `?target=&agent=` pre-fill (QuickScan.tsx
 * seeds `targetValue`/`selectedAgentId` from these on mount) rather than dispatching a scan
 * directly — the wizard already owns every step a real scan needs (self-attestation, authorization
 * window, consent), and duplicating that here would either skip those gates or re-implement them.
 */
function StartScanDialog({ agent }: { agent: InternalAgent }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState('');
  const [authorized, setAuthorized] = useState(false);
  const [touched, setTouched] = useState(false);

  const trimmed = target.trim();
  const isValidTarget = trimmed.length > 0 && isPrivateIPv4(trimmed);
  const canStart = isValidTarget && authorized;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setTarget('');
      setAuthorized(false);
      setTouched(false);
    }
  };

  const handleStart = () => {
    if (!canStart) return;
    navigate(`/quick-scan?target=${encodeURIComponent(trimmed)}&agent=${encodeURIComponent(agent.id)}`);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <ScanSearch size={14} /> Start Scan
        </Button>
      </DialogTrigger>
      <DialogContent title={`Scan via ${agent.hostname}`} description="Scan an internal IP or CIDR range reachable from this agent.">
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor={`scan-target-${agent.id}`}>Target IP or CIDR range</Label>
            <Input
              id={`scan-target-${agent.id}`}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder="192.168.1.0/24 or 192.168.1.100"
              autoFocus
            />
            <FieldError>{touched && trimmed.length > 0 && !isValidTarget ? 'Must be a private (RFC 1918) IP address or CIDR range.' : null}</FieldError>
          </div>

          <label className="flex items-start gap-3 rounded-xl border border-border p-3 text-sm">
            <input type="checkbox" className="mt-0.5" checked={authorized} onChange={(e) => setAuthorized(e.target.checked)} />
            <span className="font-medium text-ink">I confirm I am authorized to scan this network.</span>
          </label>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleStart} disabled={!canStart}>
              Start Scan
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * "Run" dialog for one Agent Capability card — same "collect a target + authorization, then hand
 * off to the Quick Scan wizard" posture as StartScanDialog above (never dispatches a scan itself),
 * plus a pre-selected `profile=internalRedTeam` so the wizard lands on the matching scan profile
 * rather than its own default ('full'). Requires a private (RFC 1918) IP for the same reason
 * StartScanDialog does — QuickScan.tsx's `internalRedTeamAvailable` guard resets the profile back
 * to 'full' the moment the target isn't recognized as an internal IP, so a bare hostname here would
 * silently lose the "matching scan profile" requirement once the wizard opens.
 */
function RunCapabilityDialog({ capabilityLabel, agent }: { capabilityLabel: string; agent: InternalAgent }) {
  const navigate = useNavigate();
  const inputId = useId();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(agent.ipAddress);
  const [authorized, setAuthorized] = useState(false);
  const [touched, setTouched] = useState(false);

  const trimmed = target.trim();
  const isValidTarget = trimmed.length > 0 && isPrivateIPv4(trimmed);
  const canStart = isValidTarget && authorized;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setTarget(agent.ipAddress);
      setAuthorized(false);
      setTouched(false);
    }
  };

  const handleStart = () => {
    if (!canStart) return;
    navigate(`/quick-scan?target=${encodeURIComponent(trimmed)}&agent=${encodeURIComponent(agent.id)}&profile=internalRedTeam`);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          Run
        </Button>
      </DialogTrigger>
      <DialogContent
        title={`Run ${capabilityLabel} via ${agent.hostname}`}
        description="Launches a Quick Scan on the Internal Red Team profile through this agent."
      >
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor={inputId}>IP address or hostname to scan</Label>
            <Input
              id={inputId}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              onBlur={() => setTouched(true)}
              autoFocus
            />
            <FieldError>{touched && trimmed.length > 0 && !isValidTarget ? 'Must be a private (RFC 1918) IP address.' : null}</FieldError>
          </div>

          <label className="flex items-start gap-3 rounded-xl border border-border p-3 text-sm">
            <input type="checkbox" className="mt-0.5" checked={authorized} onChange={(e) => setAuthorized(e.target.checked)} />
            <span className="font-medium text-ink">I confirm I am authorized to test this target.</span>
          </label>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleStart} disabled={!canStart}>
              Start
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const AGENT_CAPABILITIES = [
  { icon: Radar, label: 'Port scanning', description: 'Sensitive services (SSH, RDP, databases, etc.)' },
  { icon: ScanSearch, label: 'Service fingerprinting', description: 'Running software and versions' },
  { icon: Network, label: 'Config exposure', description: 'Sensitive paths on internal HTTP services' },
  { icon: Users, label: 'AD enumeration', description: 'Users, groups, computers via LDAP (anonymous bind)' },
  { icon: Share2, label: 'SMB checks', description: 'Signing status, null sessions, share enumeration' },
  { icon: KeyRound, label: 'Kerberos detection', description: 'Kerberoastable accounts, AS-REP roastable accounts' },
  {
    icon: Route,
    label: 'Lateral movement indicators',
    description: 'WinRM, SMB signing, RDP NLA status (requires Windows endpoint agent on target)',
  },
] as const;

function AgentCapabilities({
  connectedAgents,
  selectedAgentId,
  onSelectAgent,
}: {
  connectedAgents: InternalAgent[];
  selectedAgentId: string | null;
  onSelectAgent: (id: string) => void;
}) {
  const selectId = useId();
  const selectedAgent = connectedAgents.find((a) => a.id === selectedAgentId) ?? null;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Agent capabilities</CardTitle>
        <CardDescription>What the internal agent can test today.</CardDescription>
      </CardHeader>
      <CardContent>
        {connectedAgents.length > 1 ? (
          <div className="mb-4 flex items-center gap-2">
            <Label htmlFor={selectId} className="whitespace-nowrap text-sm font-medium text-ink">
              Run tests via:
            </Label>
            <Select value={selectedAgentId ?? undefined} onValueChange={onSelectAgent}>
              <SelectTrigger id={selectId} className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {connectedAgents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.hostname}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {AGENT_CAPABILITIES.map(({ icon: Icon, label, description }) => (
            <div key={label} className="rounded-xl border border-border bg-surface-sunken p-3">
              <span className="flex size-8 items-center justify-center rounded-full bg-brand/10 text-brand">
                <Icon size={16} />
              </span>
              <p className="mt-2 text-sm font-medium text-ink">{label}</p>
              <p className="mt-0.5 text-xs text-ink-muted">{description}</p>
              <div className="mt-3">
                {selectedAgent ? (
                  // Keyed on the agent id too: RunCapabilityDialog's `target` field seeds itself
                  // from `agent.ipAddress` only once, on mount — without this, switching the
                  // device selector would silently leave an already-mounted dialog pointed at the
                  // PREVIOUS agent's IP instead of remounting fresh for the new one.
                  <RunCapabilityDialog key={selectedAgent.id} capabilityLabel={label} agent={selectedAgent} />
                ) : (
                  <Button size="sm" variant="outline" disabled title="No connected devices">
                    Run
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

const EXPLOITATION_STATUS_BADGE: Record<(typeof EXPLOITATION_CAPABILITIES)[number]['status'], { label: string; variant: 'success' | 'neutral' }> = {
  available: { label: 'Available', variant: 'success' },
  requires_credentials: { label: 'Requires credentials', variant: 'neutral' },
};

/**
 * "Run" dialog for one Authorized Exploitation card — asks which open finding to run the
 * capability against. There is deliberately no real dispatch behind "Start" yet: no capability
 * here has a backend job-dispatch path at all (see lib/exploitation-capabilities.ts's own TODO),
 * and wiring one is out of scope for a web-only change. Rather than silently no-op or fabricate a
 * fake "started" state, Start shows an honest info toast — the dialog itself (finding picker,
 * authorization gate, Owner-only visibility) is real and ready for whenever that backend exists.
 */
function RunExploitationDialog({ capability, findings }: { capability: ExploitationCapability; findings: FindingRecord[] }) {
  const { toast } = useToast();
  const selectId = useId();
  const [open, setOpen] = useState(false);
  const [findingId, setFindingId] = useState<string | undefined>(findings[0]?.id);
  const [authorized, setAuthorized] = useState(false);

  const canStart = Boolean(findingId) && authorized;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    setAuthorized(false);
    if (next) setFindingId(findings[0]?.id);
  };

  const handleStart = () => {
    if (!canStart) return;
    const finding = findings.find((f) => f.id === findingId);
    toast({
      title: 'Not available yet',
      description: `Running "${capability.name}" against "${finding?.title ?? 'this finding'}" isn't wired up on the backend yet.`,
      variant: 'info',
    });
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" disabled={findings.length === 0} title={findings.length === 0 ? 'No relevant open findings' : undefined}>
          Run
        </Button>
      </DialogTrigger>
      <DialogContent title={`Run ${capability.name}`} description={capability.description}>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor={selectId}>Finding to run against</Label>
            <Select value={findingId} onValueChange={setFindingId}>
              <SelectTrigger id={selectId}>
                <SelectValue placeholder="Select a finding" />
              </SelectTrigger>
              <SelectContent>
                {findings.map((finding) => (
                  <SelectItem key={finding.id} value={finding.id}>
                    {finding.title} — {finding.target}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <label className="flex items-start gap-3 rounded-xl border border-border p-3 text-sm">
            <input type="checkbox" className="mt-0.5" checked={authorized} onChange={(e) => setAuthorized(e.target.checked)} />
            <span className="font-medium text-ink">I confirm I am authorized to test this target.</span>
          </label>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleStart} disabled={!canStart}>
              Start
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * "Authorized Exploitation" — targeted safe confirmation probes against already-reported findings
 * (distinct from the discovery-phase scans above). NOT dispatched yet — see
 * lib/exploitation-capabilities.ts's TODO. This card shows the correct status for each capability
 * (all four are anonymous/credential-less probes by nature, hence "Available" today — "Requires
 * credentials" exists as a status for a future capability that needs domain creds, none of the
 * current four do). "Run" is Owner-only — hidden entirely for every other role, same convention as
 * the Connected agents table's Revoke/Delete actions just above.
 */
function AuthorizedExploitation({ isOwner, openFindings }: { isOwner: boolean; openFindings: FindingRecord[] }) {
  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center gap-2">
          <FlaskConical size={18} className="text-brand" />
          <CardTitle>Authorized Exploitation</CardTitle>
        </div>
        <CardDescription>
          Run targeted safe exploitation proofs against confirmed findings. Requires Owner role and explicit authorization.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {EXPLOITATION_CAPABILITIES.map((capability) => {
            const badge = EXPLOITATION_STATUS_BADGE[capability.status];
            const relevantFindings = findingsForCapability(capability, openFindings);
            return (
              <div key={capability.id} className="flex items-start justify-between gap-4 rounded-xl border border-border bg-surface-sunken p-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">{capability.name}</p>
                  <p className="mt-0.5 text-xs text-ink-muted">{capability.description}</p>
                  <p className="mt-1 text-xs text-ink-faint">Requires: {capability.requires}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                  {isOwner ? <RunExploitationDialog capability={capability} findings={relevantFindings} /> : null}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function TokenReveal({ token, expiresAt }: InternalAgentToken) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 rounded-xl border-2 border-brand bg-surface-sunken px-3 py-2">
        <code className="truncate text-sm text-ink">{token}</code>
        <Button variant="ghost" size="sm" onClick={copy}>
          <Copy size={14} /> {copied ? 'Copied!' : 'Copy'}
        </Button>
      </div>
      <p className="text-xs font-medium text-brand">
        Save this token — it will only be shown once. Expires in 24 hours.
      </p>
      {/* Referenced by tests/hint text below only — expiresAt is shown for reference, the
          literal "24 hours" copy above comes straight from the ticket spec. */}
      <p className="text-xs text-ink-faint">Token expires {new Date(expiresAt).toLocaleString()}.</p>
    </div>
  );
}

/**
 * OS-specific install steps (Task 1A). Always visible next to the download buttons — not gated
 * behind an issued token — since a Viewer (who cannot generate a token) or anyone downloading
 * ahead of time still benefits from knowing the steps, and step 4 below only means "the CLI will
 * interactively prompt for it", not "you must already have one in hand".
 */
function InstallInstructions() {
  return (
    <div className="grid gap-4 text-sm text-ink-muted sm:grid-cols-2">
      <div>
        <p className="font-medium text-ink">Windows:</p>
        <ol className="mt-1 list-decimal space-y-1 pl-5">
          <li>Download defendiq-agent-win.exe</li>
          <li>Right-click → Run as Administrator</li>
          <li>Enter your registration token when prompted</li>
          <li>
            Done — agent installs itself and starts automatically. You will see a notification confirming it is running. It will start
            automatically after every reboot.
          </li>
        </ol>
      </div>
      <div>
        <p className="font-medium text-ink">Linux:</p>
        <ol className="mt-1 list-decimal space-y-1 pl-5">
          <li>Download defendiq-agent-linux</li>
          <li>chmod +x defendiq-agent-linux</li>
          <li>sudo ./defendiq-agent-linux</li>
          <li>Enter your registration token when prompted</li>
          <li>Done — agent installs itself as a system service and starts automatically after every reboot.</li>
        </ol>
      </div>
    </div>
  );
}

export function InternalTesting() {
  const { principal } = useAuth();
  const canManage = principal?.role === undefined || can(principal.role, 'internalAgent:manage');
  const isOwner = principal?.role === 'owner';
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [issuedToken, setIssuedToken] = useState<InternalAgentToken | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['internal-agents'],
    queryFn: () => internalAgentsApi.list(),
    refetchInterval: 30000,
  });

  // Fetched on mount — GET /v1/agent/downloads is public/unauthenticated (see
  // apps/api/src/routes/agent-downloads.ts), so this works even for a signed-out visitor. Either
  // field can be `null` if the env var isn't configured yet; the buttons below render disabled
  // with a tooltip in that case rather than linking to a dead `#`.
  const downloadsQuery = useQuery({
    queryKey: ['agent-downloads'],
    queryFn: () => agentDownloadsApi.get(),
  });
  const downloads = downloadsQuery.data;

  const tokenMutation = useMutation({
    mutationFn: () => internalAgentsApi.generateToken(),
    onSuccess: (result) => setIssuedToken(result),
    onError: (err) => toast({ title: 'Could not generate token', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => internalAgentsApi.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['internal-agents'] });
      toast({ title: 'Agent revoked', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not revoke agent', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => internalAgentsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['internal-agents'] });
      toast({ title: 'Agent removed', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not remove agent', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  const agents = query.data?.agents ?? [];
  const connectedAgents = agents.filter((a) => a.status === 'connected');

  // One findings-count query per agent, keyed by hostname — mirrors EndpointTesting.tsx's
  // identical pattern (see Findings.tsx's ?target= support).
  const findingsQueries = useQueries({
    queries: agents.map((agent) => ({
      queryKey: ['findings', 'tenant-wide', agent.hostname],
      queryFn: () => findingsApi.listForTenant(agent.hostname),
      enabled: agents.length > 0,
    })),
  });

  // Every open finding, tenant-wide (no ?target= scope) — feeds Authorized Exploitation's
  // per-capability "relevant open findings" dropdown (findingsForCapability filters further by
  // moduleId). A separate queryKey from the per-agent queries above, even though it hits the same
  // endpoint with no target argument.
  const openFindingsQuery = useQuery({
    queryKey: ['findings', 'tenant-wide-open'],
    queryFn: () => findingsApi.listForTenant(),
  });
  const openFindings = (openFindingsQuery.data?.findings ?? []).filter((f) => f.status === 'open');

  // The capability "Run tests via" device selection, pruned to a currently-connected agent every
  // render — a purely derived fallback (not a synced effect) so a previously-selected agent that
  // disconnects or is removed falls back to another connected device on its own, rather than
  // silently disabling every capability's Run button until some later effect catches up.
  const effectiveAgentId =
    selectedAgentId && connectedAgents.some((a) => a.id === selectedAgentId) ? selectedAgentId : connectedAgents[0]?.id ?? null;

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  return (
    <div>
      <Card id={CONNECT_SECTION_ID} className="mb-6">
        <CardHeader>
          <CardTitle>Connect your internal network</CardTitle>
          <CardDescription>
            Install the DefendIQ agent on any machine inside your network to enable internal security testing. No inbound
            firewall rules required.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {downloads?.windows ? (
              <Button asChild variant="outline">
                <a href={downloads.windows}>
                  <Download size={16} /> Download for Windows (.exe)
                </a>
              </Button>
            ) : (
              <Button variant="outline" disabled title="Download not yet available">
                <Download size={16} /> Download for Windows (.exe)
              </Button>
            )}
            {downloads?.linux ? (
              <Button asChild variant="outline">
                <a href={downloads.linux}>
                  <Download size={16} /> Download for Linux
                </a>
              </Button>
            ) : (
              <Button variant="outline" disabled title="Download not yet available">
                <Download size={16} /> Download for Linux
              </Button>
            )}
          </div>

          <InstallInstructions />

          {canManage ? (
            issuedToken ? (
              <TokenReveal token={issuedToken.token} expiresAt={issuedToken.expiresAt} />
            ) : (
              <Button onClick={() => tokenMutation.mutate()} disabled={tokenMutation.isPending}>
                <KeyRound size={16} /> Generate registration token
              </Button>
            )
          ) : null}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Connected agents</CardTitle>
        </CardHeader>
        <CardContent>
          {agents.length === 0 ? (
            <EmptyState
              icon={<ShieldOff size={32} />}
              title="No agents connected yet"
              description="Install the agent to get started."
              action={
                <a href={`#${CONNECT_SECTION_ID}`} className="text-sm font-medium text-brand underline-offset-2 hover:underline">
                  Go to the download section above
                </a>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-ink-faint">
                    <th className="py-2 pr-4 font-medium">Hostname</th>
                    <th className="py-2 pr-4 font-medium">OS</th>
                    <th className="py-2 pr-4 font-medium">IP Address</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 pr-4 font-medium">Last seen</th>
                    <th className="py-2 pr-4 font-medium">Findings</th>
                    <th className="py-2 pr-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent, index) => (
                    <tr key={agent.id} className="border-b border-border last:border-none">
                      <td className="py-3 pr-4 font-medium text-ink">{agent.hostname}</td>
                      <td className="py-3 pr-4 text-ink-muted">{agent.os}</td>
                      <td className="py-3 pr-4 text-ink-muted">{agent.ipAddress}</td>
                      <td className="py-3 pr-4 text-ink-muted">
                        <StatusDot connected={agent.status === 'connected'} />
                      </td>
                      <td className="py-3 pr-4 text-ink-muted">
                        {agent.lastSeen ? new Date(agent.lastSeen).toLocaleString() : 'Never'}
                      </td>
                      <td className="py-3 pr-4">
                        <FindingsCell
                          hostname={agent.hostname}
                          count={findingsQueries[index]?.data?.findings.length}
                          isLoading={findingsQueries[index]?.isLoading ?? false}
                        />
                      </td>
                      <td className="py-3 pr-4">
                        {canManage ? (
                          <div className="flex gap-2">
                            {!agent.revokedAt ? (
                              <ConfirmDialog
                                trigger={
                                  <Button size="sm" variant="danger">
                                    Revoke
                                  </Button>
                                }
                                title="Revoke this agent?"
                                description={`"${agent.hostname}" will immediately lose access — internal testing routed through it will stop. This cannot be undone; you'd need to install and register the agent again.`}
                                confirmLabel="Revoke agent"
                                variant="danger"
                                onConfirm={() => revokeMutation.mutateAsync(agent.id)}
                              />
                            ) : null}
                            {agent.status !== 'connected' ? (
                              <ConfirmDialog
                                trigger={
                                  <Button size="sm" variant="outline">
                                    Delete
                                  </Button>
                                }
                                title={`Remove ${agent.hostname} from your workspace?`}
                                description="This cannot be undone."
                                confirmLabel="Remove agent"
                                variant="danger"
                                onConfirm={() => deleteMutation.mutateAsync(agent.id)}
                              />
                            ) : null}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {connectedAgents.length > 0 ? (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Scan internal network</CardTitle>
            <CardDescription>Start an authorized scan of an internal IP or CIDR range through one of your connected agents.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {connectedAgents.map((agent) => (
                <div key={agent.id} className="flex items-center justify-between gap-4 rounded-xl border border-border bg-surface-sunken px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">{agent.hostname}</p>
                    <p className="text-xs text-ink-muted">{agent.ipAddress}</p>
                  </div>
                  <StartScanDialog agent={agent} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <AgentCapabilities connectedAgents={connectedAgents} selectedAgentId={effectiveAgentId} onSelectAgent={setSelectedAgentId} />
      <AuthorizedExploitation isOwner={isOwner} openFindings={openFindings} />
    </div>
  );
}
